import Keycloak from 'keycloak-js';

import { STORAGE_COMPANY_KEY, STORAGE_TOKEN_KEY } from '@constants';
import { env } from '@utils/env';

/**
 * Refresh once the access token has less than this much life left, rather than waiting for a 401.
 * Comfortably longer than a slow round trip, so a request never leaves with a token that expires
 * while it is in flight.
 */
const MIN_TOKEN_VALIDITY_SECONDS = 30;

/** Served from `public/`. Lets an existing realm session be picked up with no visible redirect. */
const SILENT_CHECK_SSO_PATH = 'silent-check-sso.html';

const LOGIN_ROUTE = '/login';
const ACCOUNT_NOT_LINKED_ROUTE = '/account-not-linked';

/**
 * 401 bodies from the backend's Keycloak strategy that retrying cannot fix: the token verified, but
 * the identity behind it is not one this backend will act on. Kept in sync with
 * `easybiz-backend/src/shared/keycloak/keycloak_user.service.ts`.
 */
const UNRECOVERABLE_401_MESSAGES = [
  'Keycloak identity is not linked to an EasyBiz user',
  'This client cannot be used to act as a user',
  'Service account tokens cannot be used to act as a user',
];

const keycloakUrl = env.VITE_KEYCLOAK_URL;
const keycloakRealm = env.VITE_KEYCLOAK_REALM;
const keycloakClientId = env.VITE_KEYCLOAK_CLIENT_ID;

const isKeycloakConfigured = Boolean(
  keycloakUrl && keycloakRealm && keycloakClientId
);

const keycloak = isKeycloakConfigured
  ? new Keycloak({
      url: keycloakUrl as string,
      realm: keycloakRealm as string,
      clientId: keycloakClientId as string,
    })
  : null;

/** Absolute URL inside this app, honouring the router basename. */
const appUrl = (path = ''): string => {
  const base = new URL(env.VITE_ROUTE_BASE_URL || '/', window.location.origin)
    .toString()
    .replace(/\/$/, '');

  return `${base}/${path.replace(/^\//, '')}`;
};

let initialised = false;

/**
 * Initialise OIDC. Resolves to exactly one outcome — authenticated or not — because a third
 * "still deciding" state leaking into the router is what causes redirect loops.
 *
 * An unreachable or misconfigured realm therefore resolves to "not authenticated" rather than
 * rejecting: the app still renders, and the user is sent to Keycloak when they try to log in.
 */
export const initAuth = async (): Promise<boolean> => {
  if (!keycloak) return false;
  if (initialised) return Boolean(keycloak.authenticated);

  initialised = true;

  try {
    return await keycloak.init({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: appUrl(SILENT_CHECK_SSO_PATH),
      // The silent iframe needs third-party cookies; fall back to a visible check-sso redirect
      // rather than reporting a browser that blocks them as logged out.
      silentCheckSsoFallback: true,
      pkceMethod: 'S256',
      // The backend links a realm identity to an EasyBiz user by verified email, so a token without
      // the email claim can never resolve to a user. Requested explicitly rather than relying on the
      // client's default scopes.
      scope: 'openid profile email',
      // The periodic session-status iframe depends on the same third-party cookies and logs users
      // out spuriously when they are blocked. Refreshing on demand covers the same ground.
      checkLoginIframe: false,
      enableLogging: import.meta.env.MODE !== 'production',
    });
  } catch (error) {
    console.error('Keycloak init failed; continuing unauthenticated', error);

    return false;
  }
};

const isRealmAuthenticated = (): boolean => Boolean(keycloak?.authenticated);

/**
 * The pre-Keycloak token, read only when no realm is configured at all.
 *
 * Unlike the customer portal there is no dual-token handling to preserve here — the backoffice has
 * no signup or invite flow that mints local tokens. This exists so that environments with the realm
 * switched off (the mock-server e2e specs, and local dev against a backend with legacy auth) still
 * have a way to be logged in. Once a realm is configured this is never read and never written.
 */
const getLegacyToken = (): string | null =>
  isKeycloakConfigured ? null : localStorage.getItem(STORAGE_TOKEN_KEY);

/**
 * The bearer for API calls: the realm access token — never the ID token, which the backend
 * rejects.
 */
export const getBearerToken = (): string | null =>
  keycloak?.token ?? getLegacyToken();

/** Whether there is a session to render the protected app for. */
export const isSessionActive = (): boolean => Boolean(getBearerToken());

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Give up on a refresh that has not answered in this long, and treat it as a failed one.
 *
 * keycloak-js sends the refresh grant with no abort signal, so a realm that accepts the connection
 * and then never replies leaves the promise pending until the browser's own network timeout — minutes,
 * during which every request is queued behind it in the interceptor and the UI just spins.
 */
const REFRESH_TIMEOUT_MS = 15_000;

const withRefreshTimeout = (refresh: Promise<boolean>): Promise<boolean> => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  return Promise.race([
    refresh,
    new Promise<never>((_, reject) => {
      timer = setTimeout(
        () => reject(new Error('Keycloak token refresh timed out')),
        REFRESH_TIMEOUT_MS
      );
    }),
  ]).finally(() => clearTimeout(timer));
};

/**
 * Refresh the access token if it is close to expiry.
 *
 * Concurrent callers share one refresh: several requests hitting a stale token at once must produce
 * a single refresh request, not one per request.
 */
export const ensureFreshToken = async (): Promise<void> => {
  if (!keycloak?.authenticated) return;

  if (!refreshInFlight) {
    refreshInFlight = keycloak
      .updateToken(MIN_TOKEN_VALIDITY_SECONDS)
      .finally(() => {
        refreshInFlight = null;
      });
  }

  try {
    await withRefreshTimeout(refreshInFlight);
  } catch (error) {
    // The refresh token is gone, rejected, or the realm never answered — either way there is no
    // usable token, so surface it as an ended session rather than stalling every request. Rethrow
    // too: the session-ending redirect is async, so a caller that treated this as a normal resolve
    // would still fire its request with the stale token before the redirect ever lands.
    endSession('session-expired');
    throw error;
  }
};

export const login = async (
  redirectUri: string = window.location.href
): Promise<void> => {
  // Rejecting rather than returning quietly. The caller's whole job is to hand off to the realm, so
  // resolving without navigating left /login rendering nothing, with no clue why — and a missing
  // realm config in a deployed environment is exactly the thing that should be reported, not logged
  // to a console nobody is watching.
  if (!keycloak) {
    throw new Error('Cannot log in: Keycloak is not configured');
  }

  await keycloak.login({ redirectUri });
};

/** Local caches that must not outlive a session. Realm tokens are never among them. */
const clearCachedSessionState = (): void => {
  localStorage.removeItem(STORAGE_TOKEN_KEY);
  localStorage.removeItem(STORAGE_COMPANY_KEY);
};

/**
 * End the realm session, not just our copy of it. Clearing local state alone leaves the realm
 * session alive, so the next check-sso silently logs the user straight back in — which reads as
 * "logout is broken".
 */
export const logout = async (): Promise<void> => {
  clearCachedSessionState();

  if (!keycloak?.authenticated) {
    window.location.replace(appUrl(LOGIN_ROUTE));

    return;
  }

  await keycloak.logout({ redirectUri: appUrl() });
};

type SessionEndReason =
  /** The token verified but the backend will not act on the identity behind it. */
  | 'identity-rejected'
  /** No usable token: refresh failed, or a legacy token expired. */
  | 'session-expired';

let sessionEnding = false;

/**
 * Terminal handling for an authentication failure: exactly one navigation, no retry.
 *
 * The latch matters because several in-flight requests usually fail together, and each one calls
 * this.
 */
export const endSession = (reason: SessionEndReason): void => {
  if (sessionEnding) return;
  sessionEnding = true;

  const wasRealmAuthenticated = isRealmAuthenticated();

  clearCachedSessionState();

  if (reason === 'identity-rejected') {
    // Sending them back to Keycloak would be a loop: the realm session is valid and would log them
    // straight back in, to be rejected again. Someone internal has to link the account.
    window.location.replace(appUrl(ACCOUNT_NOT_LINKED_ROUTE));

    return;
  }

  if (wasRealmAuthenticated) {
    // Drop the stale realm session too, otherwise check-sso resurrects it on the way back. If that
    // request cannot even be built we still have to leave this page: the latch above means nothing
    // else will try again, so discarding the rejection would strand the user here with every
    // request 401ing.
    keycloak
      ?.logout({ redirectUri: appUrl(LOGIN_ROUTE) })
      .catch(() => window.location.replace(appUrl(LOGIN_ROUTE)));

    return;
  }

  window.location.replace(appUrl(LOGIN_ROUTE));
};

const readErrorMessages = (data: unknown): string[] => {
  if (typeof data !== 'object' || data === null) return [];

  const { message } = data as { message?: unknown };

  if (typeof message === 'string') return [message];
  if (Array.isArray(message)) {
    return message.filter((entry): entry is string => typeof entry === 'string');
  }

  return [];
};

/**
 * Whether a 401 body names a failure that no retry or refresh will clear, so the caller can send
 * the user somewhere useful instead of bouncing them through Keycloak.
 */
const isIdentityRejected = (data: unknown): boolean =>
  readErrorMessages(data).some((message) =>
    UNRECOVERABLE_401_MESSAGES.includes(message)
  );

/** The reason a 401 from the API should end the session. */
export const classify401 = (data: unknown): SessionEndReason =>
  isIdentityRejected(data) ? 'identity-rejected' : 'session-expired';
